import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import { GetPricingSheet } from "api/rpc/2024-04/facilityAdmin/teesheet/pricingSheet";
import { CopyPricingSheetProducts } from "api/rpc/2024-04/facilityAdmin/teesheet/pricingSheetProduct";
import { TTeesheetPricingSheet } from "redux/reducers/models/teesheet";
import { convertTime } from "helpers/Helpers";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";

import Card from "components/card/Card";
import Sheet from "components/sheet/Sheet";
import Checkbox from "components/form/checkbox/Checkbox";
import DataTable from "components/dataTable/DataTable";
import { Badge } from "components/badge/Badge";
import { Select } from "components/select/index";

import { Day, DayTab } from "../TeeSheetPricingSheets";
import {
  ALL_PRICING_SHEET_APPLICATIONS,
  ALL_PRICING_SHEET_DAYS,
  getPricingSheetApplicationLabel,
  PRICING_SHEET_APPLICATIONS,
  PricingSheetApplicationFilter,
  PricingSheetDayFilter,
} from "../pricingSheetApplications";

type TPricingSheetCopyProductsModalProps = {
  open: boolean;
  pricingSheetId: number;
  variantIds: number[];
  onClose: () => void;
  onCopied?: () => void;
};

export default function PricingSheetCopyProductsModal(props: TPricingSheetCopyProductsModalProps) {
  const { open, pricingSheetId, variantIds, onClose, onCopied } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [filterDayState, setFilterDayState] = useState<PricingSheetDayFilter>(ALL_PRICING_SHEET_DAYS);
  const [filterApplication, setFilterApplication] = useState<PricingSheetApplicationFilter>(
    ALL_PRICING_SHEET_APPLICATIONS,
  );
  const [pricingSheets, setPricingSheets] = useState<TTeesheetPricingSheet[] | undefined>(undefined);
  const [selectedPricingSheetIds, setSelectedPricingSheetIds] = useState<number[]>([]);
  const [isCopying, setIsCopying] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedPricingSheetIds([]);
      setFilterDayState(ALL_PRICING_SHEET_DAYS);
      setFilterApplication(ALL_PRICING_SHEET_APPLICATIONS);
      return;
    }

    async function loadPricingSheets() {
      setPricingSheets(undefined);
      const pricingSheetRes = await GetPricingSheet(null, false);

      if (pricingSheetRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error loading pricing sheets")));
        setPricingSheets([]);
        return;
      }

      setPricingSheets(pricingSheetRes.data ?? []);
    }

    void loadPricingSheets();
  }, [dispatch, open, t]);

  function handleSelectPricingSheetId(sheetId: number) {
    setSelectedPricingSheetIds(prev => {
      if (prev.includes(sheetId)) {
        return prev.filter(id => id !== sheetId);
      }

      return [...prev, sheetId];
    });
  }

  async function handleCopyProducts() {
    if (variantIds.length === 0) {
      dispatch(showError(t("A product must be selected")));
      return;
    }

    if (selectedPricingSheetIds.length === 0) {
      dispatch(showError(t("A pricing sheet must be selected")));
      return;
    }

    setIsCopying(true);

    const postRes = await CopyPricingSheetProducts(
      {
        pricing_sheet_ids: selectedPricingSheetIds,
        variant_ids: variantIds,
      },
      true,
    );

    setIsCopying(false);

    if (postRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error copying products to pricing sheet")));
      return;
    }

    dispatch(showSuccess(t("Products successfully copied")));
    onCopied?.();
    onClose();
  }

  const { Option } = Select;

  function handleApplicationChange(value: string | number) {
    const next = String(value);
    if (
      next === ALL_PRICING_SHEET_APPLICATIONS ||
      PRICING_SHEET_APPLICATIONS.some(application => application.value === next)
    ) {
      setFilterApplication(next as PricingSheetApplicationFilter);
    }
  }

  const filteredPricingSheets =
    pricingSheets?.filter(sheet => {
      if (sheet.id === pricingSheetId) {
        return false;
      }

      if (filterDayState !== ALL_PRICING_SHEET_DAYS && sheet.day_of_week_number !== filterDayState) {
        return false;
      }

      if (filterApplication !== ALL_PRICING_SHEET_APPLICATIONS && sheet.application !== filterApplication) {
        return false;
      }

      return true;
    }) ?? [];

  return (
    <Sheet
      closable
      title={t("Copy Products to Pricing Sheet")}
      supportText={t("Choose pricing sheets to receive the selected products.")}
      size="medium"
      open={open}
      cancelText={t("Close")}
      onCancel={onClose}
      okText={t("Continue")}
      onOk={() => void handleCopyProducts()}
      okDisabled={isCopying || selectedPricingSheetIds.length === 0 || variantIds.length === 0}
    >
      <div className="pricing-sheets-copy-application-filter">
        <Select
          label={t("Application")}
          defaultValue={filterApplication}
          usePortal
          onChange={handleApplicationChange}
        >
          <Option value={ALL_PRICING_SHEET_APPLICATIONS}>{t("All")}</Option>
          {PRICING_SHEET_APPLICATIONS.map(application => (
            <Option key={application.value} value={application.value}>
              {t(application.label)}
            </Option>
          ))}
        </Select>
      </div>
      <Card>
        <div className="flex">
          <button
            type="button"
            className="flex-1 py-2 rounded-t-m"
            style={
              filterDayState === ALL_PRICING_SHEET_DAYS
                ? { backgroundColor: "#e7e7e7", borderTopLeftRadius: "7px", borderTopRightRadius: "7px" }
                : { backgroundColor: "#ffffff" }
            }
            onClick={() => setFilterDayState(ALL_PRICING_SHEET_DAYS)}
          >
            {t("All")}
          </button>
          <DayTab
            title={t("Monday")}
            value={Day.Monday}
            compare={filterDayState}
            onClick={value => setFilterDayState(value)}
          />
          <DayTab
            title={t("Tuesday")}
            value={Day.Tuesday}
            compare={filterDayState}
            onClick={value => setFilterDayState(value)}
          />
          <DayTab
            title={t("Wednesday")}
            value={Day.Wednesday}
            compare={filterDayState}
            onClick={value => setFilterDayState(value)}
          />
          <DayTab
            title={t("Thursday")}
            value={Day.Thursday}
            compare={filterDayState}
            onClick={value => setFilterDayState(value)}
          />
          <DayTab
            title={t("Friday")}
            value={Day.Friday}
            compare={filterDayState}
            onClick={value => setFilterDayState(value)}
          />
          <DayTab
            title={t("Saturday")}
            value={Day.Saturday}
            compare={filterDayState}
            onClick={value => setFilterDayState(value)}
          />
          <DayTab
            title={t("Sunday")}
            value={Day.Sunday}
            compare={filterDayState}
            onClick={value => setFilterDayState(value)}
          />
          <DayTab
            title={t("Holiday")}
            value={Day.Holiday}
            compare={filterDayState}
            onClick={value => setFilterDayState(value)}
          />
        </div>
        <DataTable
          columns={[
            { label: "" },
            { label: t("Title") },
            { label: t("Course") },
            { label: t("Application") },
            { label: t("Start Time") },
            { label: t("End Time") },
            { label: t("Day") },
          ]}
          loading={pricingSheets === undefined}
        >
          {filteredPricingSheets.map(pricingSheet => (
            <tr
              key={pricingSheet.id}
              className="clickable"
              onClick={() => handleSelectPricingSheetId(pricingSheet.id)}
            >
              <td>
                <Checkbox
                  size="medium"
                  readOnly
                  checked={selectedPricingSheetIds.includes(pricingSheet.id)}
                />
              </td>
              <td>{pricingSheet.title}</td>
              <td>{pricingSheet.course?.full_name}</td>
              <td>
                {pricingSheet.application ? (
                  <Badge size="medium" type="success">
                    {t(getPricingSheetApplicationLabel(String(pricingSheet.application)))}
                  </Badge>
                ) : null}
              </td>
              <td>{convertTime(pricingSheet.start_time)}</td>
              <td>{convertTime(pricingSheet.end_time)}</td>
              <td>{pricingSheet.day_of_week}</td>
            </tr>
          ))}
        </DataTable>
      </Card>
    </Sheet>
  );
}
